import type { FormEvent } from "react";
import { useSearchContext } from "../context/SearchContext";
import { useSearch } from "../hooks/useSearch";
import { SearchBar } from "./SearchBar";
import { PreferencePanel } from "./PreferencePanel";

interface Props {
  /** Called after a search has been kicked off (e.g. to navigate to /search). */
  onSubmitted?: () => void;
  /** Whether to show the collapsible travel-preferences panel. */
  showPreferences?: boolean;
  /** Submit button label. */
  ctaLabel?: string;
}

const IATA = /^[A-Z]{3}$/;

export function SearchForm({
  onSubmitted,
  showPreferences = true,
  ctaLabel = "Find deals",
}: Props) {
  const { state } = useSearchContext();
  const { search, loading } = useSearch();

  const canSubmit =
    state.artist.trim().length > 0 &&
    IATA.test(state.preferences.origin_airport);

  async function handleSubmit(e: FormEvent): Promise<void> {
    e.preventDefault();
    if (!canSubmit || loading) return;
    await search();
    onSubmitted?.();
  }

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-4">
      <SearchBar />
      {showPreferences && <PreferencePanel />}
      <button
        type="submit"
        disabled={!canSubmit || loading}
        className="rounded-lg bg-brand-600 px-6 py-3 text-base font-semibold text-white transition hover:bg-brand-700 disabled:cursor-not-allowed disabled:opacity-50"
      >
        {loading ? "Searching…" : ctaLabel}
      </button>
      {!canSubmit && (
        <p className="text-center text-xs text-slate-400">
          Enter an artist and a 3-letter home airport to search.
        </p>
      )}
    </form>
  );
}
